import React from "react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { Reveal } from "../components/Reveal";
import { Link } from "react-router-dom";
import {
  TrendingUp, Users, PiggyBank, Target, Building2, Cloud, UserCheck, ShieldCheck,
  Video, MonitorPlay, Layers, ArrowRight, CheckCircle2, Quote
} from "lucide-react";
import { LD_OUTCOMES, LD_USE_CASES, LD_DELIVERY_MODELS, LD_TRUST_LOGOS, STATS, COLLAB_IMG } from "../mock";

const OUT_ICONS = { TrendingUp, Users, PiggyBank, Target };
const USE_ICONS = { Building2, Cloud, UserCheck, ShieldCheck };
const DM_ICONS = { Video, Building2, MonitorPlay, Layers };

const Eyebrow = ({ children }) => (
  <span className="inline-flex items-center gap-2 text-xs font-bold tracking-widest text-[#15348C] bg-[#15348C]/[0.06] border border-[#15348C]/10 px-4 py-1.5 rounded-full">
    <span className="w-1.5 h-1.5 rounded-full bg-[#F57C20]"/>
    {children}
  </span>
);

const AllOfferingsPage = () => {
  return (
    <div className="bg-white min-h-screen">
      <Header/>

      {/* Hero */}
      <section className="relative bg-gradient-to-br from-[#0A1F5C] via-[#15348C] to-[#1E47A8] text-white overflow-hidden">
        <div
          className="absolute inset-0 opacity-[0.06]"
          style={{ backgroundImage: "radial-gradient(#fff 1px, transparent 1px)", backgroundSize: "26px 26px" }}
        />
        <div className="absolute -top-24 -right-16 w-80 h-80 bg-[#6BBE32]/20 rounded-full blur-[100px]"/>
        <div className="absolute -bottom-24 -left-16 w-80 h-80 bg-[#F57C20]/20 rounded-full blur-[100px]"/>
        <div className="relative max-w-5xl mx-auto px-4 py-24 lg:py-28 text-center">
          <Reveal>
            <span className="inline-flex items-center gap-2 text-xs font-bold tracking-widest text-[#8BD835] bg-white/5 border border-white/10 px-4 py-2 rounded-full mb-7">
              <span className="w-1.5 h-1.5 rounded-full bg-[#8BD835] sg-pulse"/>
              ENTERPRISE LEARNING SOLUTIONS
            </span>
            <h1 className="text-4xl lg:text-6xl font-bold leading-[1.1]">
              Future-Ready Workforces, Built for L&amp;D Heads
            </h1>
            <p className="mt-5 text-gray-300 text-base lg:text-lg max-w-2xl mx-auto leading-relaxed">
              A consolidated talent partner for India&rsquo;s most ambitious enterprises &mdash; helping you close skill gaps, accelerate transformation and measure ROI on every learning rupee.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Outcome metrics for decision-makers */}
      <section className="bg-white py-20 lg:py-24">
        <div className="max-w-7xl mx-auto px-4">
          <Reveal>
            <div className="text-center max-w-2xl mx-auto mb-14">
              <Eyebrow>MEASURABLE OUTCOMES</Eyebrow>
              <h2 className="mt-4 text-3xl lg:text-4xl font-bold text-[#15348C]">Built around the metrics your CFO cares about.</h2>
            </div>
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {LD_OUTCOMES.map((o, i) => {
              const Icon = OUT_ICONS[o.icon] || TrendingUp;
              return (
                <Reveal key={o.label} variant="up" delay={i * 80}>
                  <div className="sg-card group bg-white border border-gray-200/80 border-t-4 border-t-transparent hover:border-t-[#6BBE32] rounded-2xl p-7 h-full shadow-sm">
                    <div className="w-12 h-12 rounded-xl bg-[#6BBE32]/12 flex items-center justify-center mb-5 group-hover:bg-[#6BBE32] transition-colors">
                      <Icon size={22} className="text-[#6BBE32] group-hover:text-white transition-colors"/>
                    </div>
                    <div className="text-4xl font-bold text-[#15348C] tracking-tight">{o.value}</div>
                    <div className="text-sm font-semibold text-[#15348C] mt-2">{o.label}</div>
                    <p className="text-sm text-gray-500 mt-2.5 leading-relaxed">{o.desc}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* Use cases */}
      <section className="bg-[#F8F9FA] py-20 lg:py-24">
        <div className="max-w-7xl mx-auto px-4">
          <Reveal>
            <div className="text-center max-w-2xl mx-auto mb-14">
              <Eyebrow>L&amp;D USE CASES</Eyebrow>
              <h2 className="mt-4 text-3xl lg:text-4xl font-bold text-[#15348C]">Where SkillsGain delivers impact</h2>
              <p className="mt-3 text-gray-600">Programs designed for the conversations happening in your boardroom &mdash; transformation, capability, productivity.</p>
            </div>
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {LD_USE_CASES.map((u, i) => {
              const Icon = USE_ICONS[u.icon] || Building2;
              return (
                <Reveal key={u.title} variant="up" delay={i * 80}>
                  <div className="sg-card group bg-white border border-gray-200/80 rounded-2xl p-7 h-full shadow-sm">
                    <div className="w-12 h-12 rounded-xl bg-[#15348C] flex items-center justify-center mb-5 group-hover:bg-[#6BBE32] transition-colors">
                      <Icon size={22} className="text-white"/>
                    </div>
                    <h4 className="font-bold text-[#15348C] text-lg">{u.title}</h4>
                    <p className="text-sm text-gray-500 mt-2.5 leading-relaxed">{u.desc}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* Delivery models split */}
      <section className="bg-white py-20 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 grid lg:grid-cols-2 gap-14 items-center">
          <Reveal variant="left">
            <img src={COLLAB_IMG} alt="Enterprise learning" className="rounded-2xl shadow-xl ring-1 ring-black/5 w-full h-[440px] object-cover"/>
          </Reveal>
          <Reveal variant="right" delay={80}>
            <Eyebrow>FLEXIBLE DELIVERY</Eyebrow>
            <h2 className="mt-4 text-3xl lg:text-4xl font-bold text-[#15348C]">Choose the delivery model that fits your workforce.</h2>
            <p className="mt-3 text-gray-600 leading-relaxed">Hybrid, distributed, on-site or fully digital &mdash; our infrastructure scales to your global learner footprint.</p>
            <div className="mt-8 grid sm:grid-cols-2 gap-4">
              {LD_DELIVERY_MODELS.map(m => {
                const Icon = DM_ICONS[m.icon] || Video;
                return (
                  <div key={m.title} className="sg-card flex gap-3.5 bg-white border border-gray-200/80 rounded-xl p-5 shadow-sm">
                    <div className="w-10 h-10 shrink-0 rounded-lg bg-[#6BBE32]/12 flex items-center justify-center">
                      <Icon size={18} className="text-[#6BBE32]"/>
                    </div>
                    <div>
                      <div className="font-bold text-[#15348C] text-sm">{m.title}</div>
                      <p className="text-xs text-gray-500 mt-1.5 leading-relaxed">{m.desc}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </Reveal>
        </div>
      </section>

      {/* Decision-maker stats + testimonial */}
      <section className="bg-[#F8F9FA] py-20 lg:py-24">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid lg:grid-cols-5 gap-6">
            <Reveal variant="up" className="lg:col-span-2">
              <div className="bg-gradient-to-br from-[#0A1F5C] to-[#1E47A8] text-white rounded-2xl p-8 lg:p-9 h-full relative overflow-hidden">
                <div className="absolute -top-16 -right-16 w-48 h-48 bg-[#6BBE32]/20 rounded-full blur-3xl"/>
                <div className="relative">
                  <div className="text-xs font-bold tracking-widest text-[#8BD835] mb-6">BY THE NUMBERS</div>
                  <div className="grid grid-cols-2 gap-x-6 gap-y-7">
                    {STATS.map(s => (
                      <div key={s.label}>
                        <div className="text-3xl font-bold">{s.value}</div>
                        <div className="text-xs text-gray-300 mt-1">{s.label}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </Reveal>
            <Reveal variant="up" delay={100} className="lg:col-span-3">
              <div className="bg-white border border-gray-200/80 rounded-2xl p-8 lg:p-10 h-full flex flex-col shadow-sm relative overflow-hidden">
                <Quote size={80} className="text-[#F57C20]/10 absolute -top-3 -left-2" strokeWidth={1.5}/>
                <Quote size={30} className="text-[#F57C20] mb-4 relative"/>
                <p className="text-lg lg:text-xl text-[#15348C] leading-relaxed relative">
                  SkillsGain consolidated 14 fragmented training vendors into a single learning partner. We accelerated cloud certification across 4,200 engineers in 11 months and saved 38% of our annual L&amp;D spend.
                </p>
                <div className="mt-7 pt-6 border-t border-gray-200 flex flex-wrap items-center justify-between gap-4">
                  <div className="flex items-center gap-3">
                    <div className="w-11 h-11 rounded-full bg-[#15348C] text-white flex items-center justify-center font-bold text-sm shrink-0">VI</div>
                    <div>
                      <div className="font-bold text-[#15348C]">Vikram Iyer</div>
                      <div className="text-sm text-gray-500">Head of L&amp;D &bull; Fortune 500 Tech Services</div>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2 justify-end">
                    {LD_TRUST_LOGOS.slice(0,3).map(l => (
                      <span key={l} className="text-[10px] bg-[#F8F9FA] border border-gray-200 text-[#15348C] px-2.5 py-1.5 rounded-md font-semibold">{l}</span>
                    ))}
                  </div>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="bg-white py-20 lg:py-24">
        <div className="max-w-7xl mx-auto px-4">
          <Reveal variant="scale">
            <div className="bg-gradient-to-br from-[#15348C] to-[#0A1F5C] rounded-3xl p-10 lg:p-16 relative overflow-hidden">
              <div className="absolute -top-20 -right-20 w-64 h-64 bg-[#6BBE32]/20 rounded-full blur-3xl"/>
              <div className="absolute -bottom-20 -left-20 w-64 h-64 bg-[#F57C20]/20 rounded-full blur-3xl"/>
              <div className="relative grid lg:grid-cols-2 gap-10 items-center">
                <div>
                  <div className="text-xs font-bold tracking-widest text-[#8BD835] mb-3">FOR L&amp;D HEADS &amp; CHROs</div>
                  <h2 className="text-3xl lg:text-4xl font-bold text-white leading-tight">Get a tailored enterprise learning proposal.</h2>
                  <p className="mt-4 text-gray-300 leading-relaxed">Walk through your skill matrix, business goals and budget envelope with a SkillsGain advisor. We&rsquo;ll respond with a custom plan in 48 hours.</p>
                  <div className="mt-6 flex flex-wrap gap-x-5 gap-y-2.5 text-xs text-gray-300">
                    {["NDAs & MSAs ready", "GST-compliant billing", "Dedicated success manager"].map(t => (
                      <span key={t} className="flex items-center gap-1.5"><CheckCircle2 size={14} className="text-[#8BD835]"/>{t}</span>
                    ))}
                  </div>
                </div>
                <div className="flex flex-wrap gap-3 lg:justify-end">
                  <Link to="/contact" className="sg-btn-glow inline-flex items-center gap-2 bg-[#F57C20] hover:bg-[#D86810] text-white font-semibold px-6 py-3.5 rounded-lg transition-colors">
                    Talk to Our Advisor <ArrowRight size={16}/>
                  </Link>
                  <Link to="/contact" className="border-2 border-white/30 text-white font-semibold px-6 py-3.5 rounded-lg hover:bg-white/10 hover:border-[#8BD835] transition-colors">Request Corporate Proposal</Link>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <Footer/>
    </div>
  );
};

export default AllOfferingsPage;
